import { Box, Grid, Tab, Tabs, Tooltip, Typography, CircularProgress } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { ICareTeam } from '../../Models/CareTeams/CareTeam.model';
import { ITeamRole } from '../../Models/CareTeams/TeamRole.model';
import { Provider } from '../../Models/Provider/Provider.model';
import IProviderSelectOption from '../../Models/Provider/ProviderSelectOption.model';
import IRole from '../../Models/Role/Role.model';
import { RootState } from '../../reducers';
import { fetchTeams, setSelectedTeam, fetchTeamDetail } from '../../store/careTeams.slice';
import { setIsLoading } from '../../store/shared.slice';
import { apiString } from '../../utils/constants';
import Loading from '../Shared/Loading';
import AddCareTeamButton from './AddCareTeamButton';
import CareTeamDetails from './CareTeamDetails';
import { useParams, useNavigate } from 'react-router-dom';
import HelpIcon from '@mui/icons-material/Help';
import TeamOptimizer from '../ProviderTeams/TeamOptimizer/TeamOptimizer';
import optimizerHelpDocument from '../../assets/documents/TeamOptimizerHowTo.pdf';
import managingTeamsDocument from '../../assets/documents/ManagingTeams.pdf';

const columns: GridColDef[] = [
    { field: 'Name', headerName: 'Team Name', flex: 1 },
    {
        field: 'MeetingAddress',
        headerName: 'Address',
        flex: 1,
        valueGetter: (value, row: any) => {
            if (!row || !row.MeetingAddress) {
                return '';
            }
            const address = row.MeetingAddress;
            // Use FullAddress if available, otherwise construct it
            if (address.FullAddress) {
                return address.FullAddress;
            }
            const parts = [];
            if (address.Address1) parts.push(address.Address1);
            if (address.City) parts.push(address.City);
            if (address.State) parts.push(address.State);
            if (address.ZipCode) parts.push(address.ZipCode);
            return parts.length > 0 ? parts.join(', ') : '';
        },
    },
    {
        field: 'MemberCount',
        headerName: 'Members',
        valueGetter: (value, row: any) => {
            if (!row) {
                return 0;
            }
            return row.AssignedMemberIds?.length ?? 0;
        },
        type: 'number',
        flex: 0.7,
        sortable: true,
    },
    {
        field: 'TeamRoles',
        headerName: 'Providers',
        flex: 0.7,
        valueGetter: (value, row: any) => {
            if (!row || !row.TeamRoles || !Array.isArray(row.TeamRoles)) {
                return '';
            }
            return row.TeamRoles.map((teamRole: ITeamRole) => {
                return `(${teamRole.ProviderName})`;
            }).join(', ');
        },
    },
];

const CareTeamsPage: React.FC = () => {
    const { teamId } = useParams<{ teamId: string }>();
    const [providerOptions, setProviderOptions] = useState<IProviderSelectOption[]>([]);
    const [roles, setRoles] = useState<IRole[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ pageSize: 20, page: 0 });
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
    };
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { selectedTeam, careTeams } = useSelector((state: RootState) => state.careTeams);
    const isLoading = useSelector((state: RootState) => state.shared.isLoading);

    useEffect(() => {
        dispatch(setIsLoading(true));
        dispatch(setSelectedTeam(null));
        axios.get(`${apiString}/Provider/getactiveproviders`).then((response) => {
            if (response.status === 200) {
                const responseOptions = response.data.map((provider: Provider) => ({
                    ProviderId: provider.Id,
                    ProviderName: `${provider.LastName}, ${provider.FirstName}`,
                    RoleName: provider.Role.RoleName,
                    RoleId: provider.Role.Id,
                }));
                setProviderOptions(responseOptions);
            }
        });

        axios.get(`${apiString}/role/roles`).then((response) => {
            if (response.status === 200) {
                setRoles(response.data);
            }
        });

        dispatch(fetchTeams(false));
    }, [dispatch]);

    useEffect(() => {
        if (teamId && careTeams.length > 0) {
            const selectedTeam = careTeams.find((team) => team.Id === teamId);
            if (selectedTeam) {
                dispatch(fetchTeamDetail(teamId));
            }
        }
    }, [teamId, careTeams, dispatch]);

    const [teamLoadingInProgress, setTeamLoadingInProgress] = useState(false);

    const handleTeamClick = (row: ICareTeam) => {
        if (teamLoadingInProgress) return;
        setTeamLoadingInProgress(true);
        dispatch(fetchTeamDetail(row.Id));
        navigate(`/provider/careteams/${row.Id}`, { replace: true });
        // Reset loading flag after a short delay to allow data fetching to complete
        setTimeout(() => setTeamLoadingInProgress(false), 500);
    };

    const teams = (
        <Box>
            {isLoading ? (
                <Loading message={'Loading teams...'} />
            ) : (
                <Grid container sx={{ height: '100vh', overflow: 'hidden' }} spacing={2}>
                    <Grid size={4} sx={{ overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}>
                        <Box sx={{ width: '90%', p: 2, display: 'flex', flexDirection: 'column', borderRight: '1px solid #ccc', height: '100%' }}>
                            <Box sx={{ mb: 2 }}>
                                <AddCareTeamButton />
                            </Box>
                            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                                <DataGrid
                                    rows={careTeams}
                                    columns={columns}
                                    getRowId={(row) => row.Id}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    onRowClick={(params) => handleTeamClick(params.row as ICareTeam)}
                                    slots={{ toolbar: GridToolbar }}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                TeamRoles: false,
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiDataGrid-cell': {
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        },
                                        '& .MuiDataGrid-columnHeaderTitle': {
                                            whiteSpace: 'normal',
                                            wordWrap: 'break-word',
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={8} sx={{ height: '100%' }}>
                        <Box sx={{ height: '100%', overflow: 'auto', p: 2, position: 'relative' }}>
                            {teamLoadingInProgress && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        zIndex: 10,
                                        backdropFilter: 'blur(2px)',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <CircularProgress />
                                        <Typography>Loading team data...</Typography>
                                    </Box>
                                </Box>
                            )}
                            {selectedTeam ? (
                                <CareTeamDetails roles={roles} providerOptions={providerOptions} />
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Typography>Select a team to view its details.</Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Box>
    );

    return (
        <>
            <Tabs
                variant="fullWidth"
                value={selectedTab}
                indicatorColor="secondary"
                textColor="secondary"
                onChange={handleTabChange}
                aria-label="disabled tabs example"
                centered
            >
                <Tab
                    label="Team Management"
                    icon={
                        <Tooltip title="Managing Teams Help Document">
                            <HelpIcon color="primary" cursor="pointer" onClick={() => window.open(`${managingTeamsDocument}`, '_blank')} />
                        </Tooltip>
                    }
                    iconPosition="end"
                />
                <Tab
                    label="Team Optimizer"
                    icon={
                        <Tooltip title="Team Optimizer Help Document">
                            <HelpIcon color="primary" cursor="pointer" onClick={() => window.open(`${optimizerHelpDocument}`, '_blank')} />
                        </Tooltip>
                    }
                    iconPosition="end"
                />
            </Tabs>
            {selectedTab === 0 && teams}
            {selectedTab === 1 && <TeamOptimizer />}
        </>
    );
};

export default CareTeamsPage;
